// The Labor tab (docs/screens/job-view-screens-round6-labor.html frame f1, approved as drawn; brief section 5). A review
// tool, not a step: where this build's hours go, by labor class, by station or by kind of item. A class opens to its
// operations, each one line for the job with its one pooled setup (Spencer, 2026-10-06); an operation on items opens
// to the items it comes from. Every lowest line carries the jump to the one place that line is changed: an item's
// route step on the item panel, a job route step on Job setup. Sums carry no jump. The chain behind each operation's
// minutes (quantity, minutes each, setup) is behind its i dot.

import { Fragment, type ReactNode } from 'react';
import { gatherLabor, laborByOperation, type OperationLine, type ResolvedGroup, type ResolvedStep } from '@zerogap/engine';
import { qty } from '../../../lib/format';
import { Chain, Info, Money, useFolds } from '../../../ui/kit';
import { groupAddress, operationAddress, UnverifiedTag } from '../../../ui/unverified';
import type { JobCtx } from '../Workspace';
import { byNumber, h1, Jump, TabHead, Tot, useChoice } from './common';

type LaborView = 'class' | 'station' | 'item';
const VIEWS = ['class', 'station', 'item'] as const;
const BAR = 190;

type RouteSource = { kind: 'route'; key: string; lineId: string; minutes: number; cents: number; note: string | null };
type ItemSource = { kind: 'item'; key: string; itemId: string; number: string; name: string; stepKey: string; minutes: number; cents: number; sort: number };
/** One lowest line under an operation: a job route step, or one item's share of the operation. */
type Source = RouteSource | ItemSource;

/** Where an operation's minutes come from, across the job: the job route's lines, then each item, its shares added up. */
function sourcesOf(groups: ResolvedGroup[], match: (s: ResolvedStep) => boolean, itemsById: Map<string, { number: string; name: string; sort: number }>): Source[] {
  const route: RouteSource[] = [];
  const items = new Map<string, ItemSource>();
  for (const g of groups) {
    for (const s of g.steps) {
      if (!match(s)) continue;
      if (g.kind === 'job') {
        if (Number(s.minutes) > 0 || Number(s.qty) > 0 || s.cents !== 0) route.push({ kind: 'route', key: s.key, lineId: s.step_id ?? '', minutes: Number(s.minutes), cents: s.cents, note: s.note });
        continue;
      }
      for (const [id, sh] of Object.entries(s.shares)) {
        const m = Number(sh.minutes ?? 0);
        if (m === 0 && sh.cents === 0 && Number(sh.qty ?? 0) === 0) continue;
        const it = itemsById.get(id);
        const e = items.get(id);
        if (e) {
          e.minutes += m;
          e.cents += sh.cents;
        } else items.set(id, { kind: 'item', key: `${s.key}|${id}`, itemId: id, number: it?.number ?? '?', name: it?.name ?? 'item', stepKey: s.key, minutes: m, cents: sh.cents, sort: it?.sort ?? 0 });
      }
    }
  }
  return [...route, ...[...items.values()].sort(byNumber)];
}

export function LaborTab({ ctx }: { ctx: JobCtx }) {
  const res = ctx.r.res!;
  const lib = ctx.r.lib!;
  const jobTypeId = (ctx.data.job?.job_type_id as string | null) ?? null;
  const [view, setView] = useChoice<LaborView>(`job.${ctx.jobId}.labor`, 'class', VIEWS);
  const folds = useFolds(`job.${ctx.jobId}.labor.${view}`);
  const itemsById = new Map(res.items.map((i) => [i.id, i]));
  const groups = res.groups.filter((g) => g.steps.length > 0);
  const jobMinutes = groups.filter((g) => g.kind === 'job').reduce((n, g) => n + Number(g.labor_minutes), 0);
  const allMinutes = Number(res.totals.labor_minutes);

  const head = (
    <TabHead
      ctx={ctx}
      figure={(r) => h1(r.totals.labor_minutes)}
      groups={[
        { key: 'class', label: 'By class' },
        { key: 'station', label: 'By station' },
        { key: 'item', label: 'By kind of item' },
      ]}
      group={view}
      onGroup={setView}
      totals={
        <>
          <Tot value={h1(allMinutes)} label="hours, this build" />
          <Tot value={<Money cents={res.totals.labor_cents} />} label="labor cost" />
          <Tot sub value={h1(jobMinutes)} label="job route" />
          <Tot sub value={`${((Math.round(allMinutes / 6) - Math.round(jobMinutes / 6)) / 10).toFixed(1)} h`} label="on items" />
        </>
      }
    />
  );
  if (groups.length === 0)
    return (
      <div className="bd">
        {head}
        <div className="tab">
          <p className="none">No labor under this build yet. Add items and fill in their inputs, or type the job route on Job setup.</p>
        </div>
      </div>
    );

  const className = (id: string | null) => (id ? lib.laborClasses[id]?.name ?? 'Labor class removed' : 'No labor class');
  const stationName = (id: string | null) => (id ? lib.workCenters[id]?.name ?? 'Station removed' : 'No station');
  // beside "job route" on an operation's line: its phase (Install), else the other of the two names (its station under a class, its class under a station)
  const otherName = (l: { labor_class_id: string | null; work_center_id: string | null; phase_id: string | null }) =>
    (l.phase_id ? lib.phases[l.phase_id]?.name : null) ?? (view === 'station' ? className(l.labor_class_id) : stationName(l.work_center_id));
  const routeJump = (lineId: string, opName: string) => <Jump ctx={ctx} home="Job setup · route" full={`Job setup › Job route › ${opName}`} to={{ tab: 'setup', lit: `route:${lineId}` }} back="labor" />;

  /** The row of one lowest line: an item's share of a step, or a job route line. */
  const leaf = (s: Source, opName: string): ReactNode =>
    s.kind === 'item' ? (
      <tr key={s.key} className="it">
        <td />
        <td className="nm">
          <span className="no">{s.number}</span> · {s.name}
        </td>
        <td className="r">{h1(s.minutes)}</td>
        <td />
        <td className="r">
          <Money cents={s.cents} />
        </td>
        <td className="src">its route</td>
        <td className="jc">
          <Jump ctx={ctx} home={`${s.number} · route`} full={`${s.number} › Route › ${opName}`} to={{ tab: 'item', item: s.itemId, lit: `step:${s.stepKey}` }} back="labor" />
        </td>
      </tr>
    ) : (
      <tr key={s.key} className="it">
        <td />
        <td className="nm">
          <span className="no">Job route</span>
          {s.note ? ` · ${s.note}` : ''}
        </td>
        <td className="r">{h1(s.minutes)}</td>
        <td />
        <td className="r">
          <Money cents={s.cents} />
        </td>
        <td className="src">its line on Job setup</td>
        <td className="jc">{routeJump(s.lineId, opName)}</td>
      </tr>
    );

  /** Where an operation line's figures come from: quantity, minutes each, setup. */
  const opChain = (l: OperationLine) => (
    <Info left>
      <div>
        {qty(l.qty)} {l.unit ?? ''} across the job × {qty(l.minutes_per_unit)} min each, plus {qty(l.setup_minutes)} min setup = {qty(l.minutes)} min.
      </div>
      {l.groups > 1 && <div>From {l.groups} kinds of item.</div>}
      <div className="muted" style={{ marginTop: 6 }}>
        The quantity is added across every item and rounded up to whole units once for the job; the setup is paid once per job, or once per batch of that total.
      </div>
    </Info>
  );

  /** An operation under a class or a station: one line for the job, open to the items (and job route lines) it comes from. */
  const opRows = (bucketKey: string, l: OperationLine, most: number): ReactNode => {
    const srcs = sourcesOf(res.groups, (s) => s.operation_id === l.operation_id, itemsById);
    const route = srcs.filter((s): s is RouteSource => s.kind === 'route');
    const items = srcs.filter((s) => s.kind === 'item');
    const single = route.length === 1 && items.length === 0 ? route[0]! : null;
    const opens = !single && srcs.length > 0;
    const k = `o:${bucketKey}:${l.operation_id}`;
    const open = opens && folds.isOpen(k);
    const n = `${items.length} ${items.length === 1 ? 'item' : 'items'}`;
    const from = items.length === 0 ? `job route · ${otherName(l)}` : route.length === 0 ? `items · ${n}` : `job route and ${n}`;
    return (
      <Fragment key={k}>
        <tr className={`op${opens ? ' opens' : ''}${open ? ' open' : ''}`} onClick={opens ? () => folds.toggle(k) : undefined}>
          <td className="cv">
            <span className={`chev${opens ? (open ? ' open' : '') : ' none'}`} />
          </td>
          <td className="nm">
            {l.operation_name} {opChain(l)} {!l.verified && Number(l.minutes) > 0 && <UnverifiedTag items={res.items} to={operationAddress(res, l.operation_id, jobTypeId)} title={`operation: ${l.operation_name}${l.groups > 1 ? `, from ${l.groups} kinds of item` : ''}`} />}
            {l.warnings.map((w) => (
              <div key={w} className="warn">
                {w}
              </div>
            ))}
          </td>
          <td className="r">{h1(l.minutes)}</td>
          <td className="bc">
            <span className="hbar lo" style={{ width: most > 0 ? Math.round((Number(l.minutes) / most) * BAR) : 0 }} />
          </td>
          <td className="r">
            <Money cents={l.cents} />
          </td>
          <td className="src">{from}</td>
          <td className="jc">{single && routeJump(single.lineId, l.operation_name)}</td>
        </tr>
        {open && srcs.map((s) => leaf(s, l.operation_name))}
      </Fragment>
    );
  };

  let body: ReactNode;
  let count: string;
  if (view !== 'item') {
    const lines = laborByOperation(res.groups).filter((l) => Number(l.minutes) > 0 || Number(l.qty) > 0);
    const buckets = gatherLabor(lines, view === 'class' ? 'labor_class_id' : 'work_center_id').sort((a, b) => Number(b.minutes) - Number(a.minutes));
    const most = Math.max(0, ...buckets.map((b) => Number(b.minutes)));
    count = `${buckets.length} ${view === 'class' ? (buckets.length === 1 ? 'class' : 'classes') : buckets.length === 1 ? 'station' : 'stations'}`;
    body = buckets.map((b) => {
      const key = b.key ?? 'none';
      const k = `c:${key}`;
      const open = folds.isOpen(k);
      const ops = [...b.lines].sort((x, y) => Number(y.minutes) - Number(x.minutes));
      return (
        <Fragment key={k}>
          <tr className={`cls${open ? ' open' : ''}`} onClick={() => folds.toggle(k)}>
            <td className="cv">
              <span className={`chev${open ? ' open' : ''}`} />
            </td>
            <td className="nm">{view === 'class' ? className(b.key) : stationName(b.key)}</td>
            <td className="r">{h1(b.minutes)}</td>
            <td className="bc">
              <span className="hbar" style={{ width: most > 0 ? Math.round((Number(b.minutes) / most) * BAR) : 0 }} />
            </td>
            <td className="r">
              <Money cents={b.cents} />
            </td>
            <td className="ql">
              {ops.length} {ops.length === 1 ? 'operation' : 'operations'}
            </td>
            <td className="jc" />
          </tr>
          {open && ops.map((l) => opRows(key, l, most))}
        </Fragment>
      );
    });
  } else {
    const sorted = [...groups].sort((a, b) => Number(b.labor_minutes) - Number(a.labor_minutes));
    const most = Math.max(0, ...sorted.map((g) => Number(g.labor_minutes)));
    count = `${sorted.length} ${sorted.length === 1 ? 'kind' : 'kinds'}`;
    body = sorted.map((g) => {
      const k = `g:${g.key}`;
      const open = folds.isOpen(k);
      return (
        <Fragment key={k}>
          <tr className={`cls${open ? ' open' : ''}`} onClick={() => folds.toggle(k)}>
            <td className="cv">
              <span className={`chev${open ? ' open' : ''}`} />
            </td>
            <td className="nm">{g.kind === 'job' ? 'Job route' : `${g.name}${g.recipe_name ? ` · ${g.recipe_name}` : ''}`}</td>
            <td className="r">{h1(g.labor_minutes)}</td>
            <td className="bc">
              <span className="hbar" style={{ width: most > 0 ? Math.round((Number(g.labor_minutes) / most) * BAR) : 0 }} />
            </td>
            <td className="r">
              <Money cents={g.labor_cents} />
            </td>
            <td className="ql">{g.kind === 'job' ? `${g.steps.length} steps, typed on Job setup` : `${g.steps.length} steps · ${g.item_ids.length} ${g.item_ids.length === 1 ? 'item' : 'items'}`}</td>
            <td className="jc" />
          </tr>
          {open &&
            g.steps.map((st) => {
              const sk = `s:${st.key}`;
              const srcs = g.kind === 'job' ? [] : sourcesOf([g], (s) => s.key === st.key, itemsById);
              const opens = srcs.length > 0;
              const so = opens && folds.isOpen(sk);
              return (
                <Fragment key={sk}>
                  <tr className={`op${opens ? ' opens' : ''}${so ? ' open' : ''}`} onClick={opens ? () => folds.toggle(sk) : undefined}>
                    <td className="cv">
                      <span className={`chev${opens ? (so ? ' open' : '') : ' none'}`} />
                    </td>
                    <td className="nm">
                      {st.operation_name}{' '}
                      <Chain
                        left
                        expr={st.qty_expr}
                        trace={st.trace}
                        note={
                          <>
                            {qty(st.qty)} {st.unit ?? ''}
                            {st.pool_qty !== st.qty ? ` of ${qty(st.pool_qty)} on the job` : ''} × {qty(st.minutes_per_unit)} min each, plus {qty(st.setup_minutes)} min setup{st.setup_batches > 1 ? ` (${st.setup_batches} batches)` : ''}.
                            {st.note ? <div>{st.note}</div> : null}
                          </>
                        }
                      />{' '}
                      {!st.verified && Number(st.minutes) > 0 && <UnverifiedTag items={res.items} to={groupAddress(g, st.step_id, jobTypeId)} title={`operation: ${st.operation_name}`} />}
                      {st.warnings.map((w) => (
                        <div key={w} className="warn">
                          {w}
                        </div>
                      ))}
                    </td>
                    <td className="r">{h1(st.minutes)}</td>
                    <td className="bc">
                      <span className="hbar lo" style={{ width: most > 0 ? Math.round((Number(st.minutes) / most) * BAR) : 0 }} />
                    </td>
                    <td className="r">
                      <Money cents={st.cents} />
                    </td>
                    <td className="src">{g.kind === 'job' ? `job route · ${otherName(st)}` : `${className(st.labor_class_id)} · ${srcs.length} ${srcs.length === 1 ? 'item' : 'items'}`}</td>
                    <td className="jc">{g.kind === 'job' && routeJump(st.step_id ?? '', st.operation_name)}</td>
                  </tr>
                  {so && srcs.map((s) => leaf(s, st.operation_name))}
                </Fragment>
              );
            })}
        </Fragment>
      );
    });
  }

  return (
    <div className="bd">
      {head}
      <div className="tab">
        <div className="bd-scroll">
          <table className="lt bd-labor">
            <colgroup>
              <col style={{ width: 22 }} />
              <col />
              <col style={{ width: 76 }} />
              <col style={{ width: 206 }} />
              <col style={{ width: 112 }} />
              <col style={{ width: 236 }} />
              <col style={{ width: 150 }} />
            </colgroup>
            <thead>
              <tr>
                <th />
                <th>{view === 'class' ? 'Labor class' : view === 'station' ? 'Station' : 'Kind of item'}</th>
                <th className="r">Hours</th>
                <th className="bc" />
                <th className="r">Labor</th>
                <th>From</th>
                <th className="r">Jump to</th>
              </tr>
            </thead>
            <tbody>{body}</tbody>
            <tfoot>
              <tr className="tot">
                <td />
                <td>{view === 'class' ? 'All classes' : view === 'station' ? 'All stations' : 'All kinds of item'}</td>
                <td className="r">{h1(allMinutes)}</td>
                <td />
                <td className="r">
                  <Money cents={res.totals.labor_cents} />
                </td>
                <td className="ql">{count}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
